/**
 * Im Fliesstext genannte Personen finden (#15).
 *
 * Gemeldet am 19.08.2026 von Marlene, präzisiert von Paul am 20.08.: „wenn im
 * body einer email ein name steht, dass dieser zB als Ansprechpartner für das
 * Projekt angelegt werden kann oder als kontakt generell."
 *
 * Der Fall aus einer echten Mail:
 *
 *   „Koordination mit Hausmeister Herr Testkamp (0176/04069001) für
 *    Zugänglichkeit."
 *
 * Herr Testkamp ist weder Absender noch Kunde — aber der Mann, den der Monteur
 * anrufen muss. Der Absender-Extraktor (#10, Vorbefüllung aus der Signatur)
 * findet ihn nicht: er sucht beschriftete Felder und die Signatur.
 *
 * 🔴 Bewusst konservativ: gesucht wird nur, was durch eine **Anrede**
 * eingeleitet ist („Herr", „Frau"). Alles andere — Wortpaare mit zwei grossen
 * Anfangsbuchstaben — trifft Strassennamen, Firmen, Produktbezeichnungen und
 * Wochentage. Ein Vorschlag, der zu oft danebenliegt, wird nach drei Tagen
 * ignoriert, und dann ist die Funktion wertlos.
 *
 * Es bleibt ein Vorschlag: angelegt wird erst nach Bestätigung im Dialog.
 */

export type PersonImText = {
  anrede: "Herr" | "Frau"
  vorname: string
  nachname: string
  telefon: string | null
  /** Der Satzausschnitt, in dem die Person steht — als Beleg für den Nutzer. */
  fundstelle: string
}

// „Herr Testkamp", „Frau Dr. Sauer", „Herrn Klaus Meier"
// Vorname optional, Titel werden übersprungen.
const ANREDE = /\b(Herrn?|Frau)\s+((?:(?:Dr|Prof|Dipl)\.-?(?:Ing\.)?\s+)?)([A-ZÄÖÜ][a-zäöüß]+)(?:\s+([A-ZÄÖÜ][a-zäöüß]+(?:-[A-ZÄÖÜ][a-zäöüß]+)?))?/g

// Telefonnummer im direkten Umfeld: in Klammern oder nach Komma/Bindestrich.
const TELEFON = /(\+?\d[\d\s/\-()]{7,}\d)/

/**
 * Wörter, die nach einer Anrede stehen können, ohne ein Name zu sein.
 * „Frau Müller" ja, „Frau Ihrer" nein.
 */
const KEIN_NAME = new Set([
  "ihrer", "ihres", "ihrem", "seiner", "seines", "meiner", "unserer",
  "dieser", "welche", "welcher", "oder", "und", "vom", "von", "aus",
])

function saubereFundstelle(text: string, index: number): string {
  const start = Math.max(0, text.lastIndexOf(".", index) + 1)
  const endeRoh = text.indexOf(".", index)
  const ende = endeRoh === -1 ? Math.min(text.length, index + 120) : endeRoh + 1
  return text.slice(start, ende).replace(/\s+/g, " ").trim().slice(0, 160)
}

export function personenImText(bodyText?: string | null): PersonImText[] {
  if (!bodyText) return []
  // Zitierte Vorgeschichte abschneiden: dort stehen alte Namen, die mit
  // dieser Mail nichts zu tun haben.
  const text = bodyText
    .split(/^(?:>|Von:|Gesendet:|-{3,}\s*Urspr)/m)[0]
    .slice(0, 20000)

  const gefunden: PersonImText[] = []
  const gesehen = new Set<string>()

  for (const m of text.matchAll(ANREDE)) {
    const [ganz, anredeRoh, , wort1, wort2] = m
    const nachnameRoh = (wort2 || wort1).trim()
    const vorname = wort2 ? wort1.trim() : ""
    if (KEIN_NAME.has(wort1.toLowerCase())) continue
    if (nachnameRoh.length < 3) continue

    const schluessel = `${vorname}|${nachnameRoh}`.toLowerCase()
    if (gesehen.has(schluessel)) continue
    gesehen.add(schluessel)

    // Telefonnummer nur, wenn sie dicht dahinter steht — sonst greift man sich
    // die Nummer aus der Signatur und haengt sie der falschen Person an.
    const umfeld = text.slice(m.index! + ganz.length, m.index! + ganz.length + 40)
    const tel = umfeld.match(TELEFON)

    gefunden.push({
      anrede: anredeRoh.startsWith("Frau") ? "Frau" : "Herr",
      vorname,
      nachname: nachnameRoh,
      telefon: tel ? tel[1].trim() : null,
      fundstelle: saubereFundstelle(text, m.index!),
    })
  }
  return gefunden
}

/**
 * Nur die, die nicht ohnehin schon der Absender sind.
 *
 * Sonst schlaegt das Portal vor, den Absender als Ansprechpartner anzulegen —
 * den gibt es dafuer seit #10 den eigenen, besseren Weg.
 */
export function ohneAbsender(
  personen: PersonImText[],
  absender?: string | null,
): PersonImText[] {
  const a = (absender || "").toLowerCase()
  if (!a) return personen
  return personen.filter((p) => !a.includes(p.nachname.toLowerCase()))
}
